<template>
  <el-form
    ref="formRef"
    :model="formData"
    :rules="rules"
    label-width="120px"
    class="interview-form"
  >
    <el-form-item label="面试阶段" prop="stage">
      <el-select v-model="formData.stage" placeholder="请选择面试阶段" clearable class="form-select">
        <el-option
          v-for="(label, value) in INTERVIEW_STAGES"
          :key="value"
          :label="label"
          :value="value"
          :disabled="value === 'completed'"
        />
      </el-select>
    </el-form-item>

    <el-form-item label="面试时间" prop="dateTime" required>
      <el-date-picker
        v-model="formData.dateTime"
        type="datetime"
        placeholder="选择面试时间"
        format="YYYY-MM-DD HH:mm"
        value-format="YYYY-MM-DDTHH:mm:00"
        :disabled-date="disabledDate"
        class="form-date-picker"
      />
    </el-form-item>

    <el-row :gutter="20">
      <el-col :span="12">
        <el-form-item label="时长(分钟)" prop="duration">
          <el-input-number 
            v-model="formData.duration" 
            :min="15" 
            :max="240" 
            :step="15"
            class="form-number-input" 
          />
        </el-form-item>
      </el-col>
      <el-col :span="12">
        <el-form-item label="面试状态" prop="status">
          <el-select 
            v-model="formData.status" 
            placeholder="请选择状态" 
            clearable
            class="form-select"
          >
            <el-option
              v-for="(label, value) in INTERVIEW_STATUS"
              :key="value"
              :label="label"
              :value="value"
            />
          </el-select>
        </el-form-item>
      </el-col>
    </el-row>

    <el-row :gutter="20">
      <el-col :span="12">
        <el-form-item label="面试地点" prop="location">
          <el-input 
            v-model="formData.location" 
            placeholder="如：线上、公司会议室等" 
            clearable
            class="form-input"
          />
        </el-form-item>
      </el-col>
      <el-col :span="12">
        <el-form-item label="面试官" prop="interviewer">
          <el-input 
            v-model="formData.interviewer" 
            placeholder="面试官姓名" 
            clearable
            class="form-input"
          />
        </el-form-item>
      </el-col>
    </el-row>

    <el-form-item label="准备材料" prop="preparationMaterials">
      <el-input
        v-model="formData.preparationMaterials"
        type="textarea"
        placeholder="面试准备材料和注意事项"
        :rows="3"
        clearable
        class="form-textarea"
      />
    </el-form-item>

    <el-form-item label="备注" prop="notes">
      <el-input
        v-model="formData.notes"
        type="textarea"
        placeholder="其他需要记录的信息"
        :rows="3"
        clearable
        class="form-textarea"
      />
    </el-form-item>

    <el-form-item v-if="editing" label="面试反馈" prop="feedback">
      <el-input
        v-model="formData.feedback"
        type="textarea"
        placeholder="面试后的反馈和评价"
        :rows="3"
        clearable
        class="form-textarea"
      />
    </el-form-item>

    <el-form-item class="form-buttons">
      <el-button type="primary" @click="submitForm" :loading="loading">
        {{ editing ? '更新' : '创建' }}
      </el-button>
      <el-button @click="resetForm">重置</el-button>
    </el-form-item>
  </el-form>
</template>

<script setup lang="ts">
import { ref, reactive, defineProps, defineEmits, watch } from 'vue';
import type { FormInstance, FormRules } from 'element-plus';
import { ElMessage } from 'element-plus';
import type { Interview } from '@/types/interview';
import { INTERVIEW_STAGES, INTERVIEW_STATUS } from '@/types/interview';
import interviewService from '@/services/interviewService';

const props = defineProps<{
  positionId: number;
  interview?: Interview;
  editing?: boolean;
}>();

const emit = defineEmits<{
  (e: 'created', interview: Interview): void;
  (e: 'updated', interview: Interview): void;
  (e: 'cancel'): void;
}>();

const formRef = ref<FormInstance>();
const loading = ref(false);

// 初始化表单数据
const initialFormData = () => {
  const baseData = {
    stage: 'not_started',
    dateTime: '',
    duration: 60,
    location: '',
    interviewer: '',
    notes: '',
    status: 'pending',
    preparationMaterials: '',
    feedback: ''
  };

  // 只在创建时添加positionId
  return props.editing ? baseData : { ...baseData, positionId: props.positionId };
};

const formData = reactive<Interview>(initialFormData());

// 禁用过去的日期
const disabledDate = (time: Date) => {
  return time.getTime() < Date.now() - 8.64e7; // 禁用今天之前的日期
};

// 当编辑模式且提供了面试数据时，填充表单
watch(
  () => props.interview,
  (newVal) => {
    if (newVal && props.editing) {
      Object.assign(formData, { ...initialFormData(), ...newVal });
    }
  },
  { immediate: true }
);

// 表单验证规则
const rules = reactive<FormRules>({
  stage: [{ required: true, message: '请选择面试阶段', trigger: 'change' }],
  dateTime: [{ required: true, message: '请选择面试时间', trigger: 'change' }],
  duration: [{ type: 'number', message: '面试时长必须为数字', trigger: 'blur' }],
  location: [{ max: 100, message: '面试地点不能超过100个字符', trigger: 'blur' }],
  interviewer: [{ max: 50, message: '面试官姓名不能超过50个字符', trigger: 'blur' }],
  preparationMaterials: [{ max: 500, message: '准备材料不能超过500个字符', trigger: 'blur' }],
  notes: [{ max: 500, message: '备注不能超过500个字符', trigger: 'blur' }],
  feedback: [{ max: 1000, message: '面试反馈不能超过1000个字符', trigger: 'blur' }]
});

// 提交表单
const submitForm = async () => {
  if (!formRef.value) return;
  
  try {
    const valid = await formRef.value.validate();
    if (!valid) {
      console.warn('表单验证未通过:', formRef.value.getValidateFields());
      ElMessage.warning('请完善表单信息');
      return;
    }

    loading.value = true;
    console.log('提交面试表单数据:', JSON.stringify(formData));
    let response;
    
    if (props.editing && props.interview?.id) {
      // 更新面试时，从formData中提取需要的字段
      const { positionId, createTime, updateTime, jobPosition, ...updateData } = formData;
      response = await interviewService.updateInterview({
        id: props.interview.id,
        ...updateData
      });
    } else {
      // 创建面试时使用完整的formData
      response = await interviewService.createInterview(formData);
    }

    ElMessage.success(props.editing ? '面试更新成功' : '面试创建成功');
    // 重置表单
    resetForm();
    // 发送更新事件并传递响应数据
    if (props.editing) {
      emit('updated', response);
    } else {
      emit('created', response);
    }
  } catch (error) {
    console.error('面试操作失败:', error);
    ElMessage.error('操作失败，请重试');
  } finally {
    loading.value = false;
  }
};

// 重置表单
const resetForm = () => {
  if (formRef.value) {
    formRef.value.resetFields();
  }
};
</script>

<style scoped>
.interview-form {
  max-width: 800px;
  margin: 0 auto;
  padding: 24px;
}

.form-select,
.form-date-picker,
.form-input,
.form-number-input {
  width: 100%;
}

.form-textarea {
  font-family: inherit;
}

.form-buttons {
  margin-top: 24px;
  text-align: center;
}

:deep(.el-form-item__label) {
  font-weight: 500;
}

:deep(.el-input__wrapper),
:deep(.el-textarea__inner) {
  box-shadow: 0 0 0 1px #dcdfe6;
}

:deep(.el-input__wrapper:hover),
:deep(.el-textarea__inner:hover) {
  box-shadow: 0 0 0 1px var(--el-color-primary);
}

:deep(.el-select .el-input__wrapper) {
  box-shadow: 0 0 0 1px #dcdfe6 !important;
}

:deep(.el-select .el-input__wrapper:hover) {
  box-shadow: 0 0 0 1px var(--el-color-primary) !important;
}
</style> 